import { useState, type FormEvent } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@afrilink/ui';
import { salesService, type LeadNote } from '@/domains/sales/services/salesService';

/**
 * AddNoteForm — reusable multi-line note composer for a sales lead/customer.
 *
 * Notes are append-only (no edit/delete) and attributed to the caller by the
 * `lead-add-note` EF. The EF has no sales_stage guard, so this form works on
 * leads at any stage — including converted (`customer`) and `lost` records —
 * subject to the EF's per-lead scope check (own/assigned lead, manager's
 * company, or admin). Approved 2026-07-08: sales notes are an explicit
 * exception to the LD-S11 read-only boundary on converted customers.
 */
export function AddNoteForm({
  customerProfileId,
  onAdded,
  placeholder = 'Add a note (call outcome, next steps…)',
  autoFocus = false,
}: {
  customerProfileId: string;
  onAdded?: (note: LeadNote) => void;
  placeholder?: string;
  autoFocus?: boolean;
}) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = text.trim();
    if (trimmed.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const note = await salesService.addNote(customerProfileId, trimmed);
      setText('');
      onAdded?.(note);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={onSubmit} className="space-y-2">
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        disabled={busy}
        maxLength={4000}
        rows={3}
        autoFocus={autoFocus}
        placeholder={placeholder}
        className="w-full rounded border border-input bg-card px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      />
      {error && <p className="text-xs text-danger">{error}</p>}
      <div className="flex justify-end">
        <Button type="submit" variant="primary" size="sm" disabled={busy || text.trim().length === 0}>
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          Add note
        </Button>
      </div>
    </form>
  );
}
